import { Combobox, Listbox, Transition } from "@headlessui/react";
import { IconCheck, IconChevronDown, IconX } from "@tabler/icons-react";
import classNames from "classnames";
import {
  CSSProperties,
  Fragment,
  KeyboardEventHandler,
  ReactNode,
  forwardRef,
  useState,
} from "react";
import { SelectProps } from "@common/types";
import { useDocumentDirection } from "@app/hooks";
import { Loading, LoadingDots } from "../loading";
import { Text } from "../text";
import { Label } from "./Label";

type ListBoxType = typeof Listbox;

type Props<
  Value,
  isMulti extends boolean = false,
  isSearchable extends boolean = false,
> = SelectProps<Value, isMulti, isSearchable> & {
  className?: string;
  by?: (a: Value, b: Value) => boolean;
};

const SelectRoot = forwardRef(function SelectRoot<
  Value,
  isMulti extends boolean = false,
  isSearchable extends boolean = false,
>(
  {
    options,
    label,
    value,
    multiple,
    placeholder,
    icon,
    iconPosition = "start",
    actionLabel,
    getLabel,
    getKey,
    maxItemShow,
    error,
    children,
    searchable,
    searchFilter,
    loading,
    className,
    size,
    hideMenu,
    onSubmit,
    onInputChange,
    menuPlacement,
    success,
    autoWidth,
    ...props
  }: Props<Value, isMulti, isSearchable>,
  ref: any,
) {
  const [query, setQuery] = useState("");
  const controlDir = useDocumentDirection();
  const portalDir = controlDir;
  const optionsAnchorSide = controlDir === "rtl" ? "end" : "start";
  const isFilterControl =
    typeof className === "string" && className.includes("nh-filter-control");
  const wrapMultiple = multiple && !isFilterControl;
  const optionsAnchor = {
    to: `${menuPlacement === "top" ? "top" : "bottom"} ${optionsAnchorSide}`,
    gap: 6,
    padding: 16,
  } as const;

  const isSelected = (option: Value) =>
    value
      ? multiple
        ? (value as Value[]).includes(option)
        : value === option
      : false;

  const getLabelInternal = getLabel || ((value: any) => value.label);
  const getOptionKey = (option: Value, index?: number) => {
    if (getKey) {
      return getKey(option);
    }

    if (
      option === null ||
      ["string", "number", "boolean"].includes(typeof option)
    ) {
      return String(option);
    }

    const optionRecord = option as Record<string, unknown>;
    const key =
      optionRecord.value ?? optionRecord.id ?? optionRecord.key ?? index;

    return String(key);
  };

  const filteredOptions =
    query === ""
      ? options || []
      : (options || []).filter((option, index) => {
          return searchFilter
            ? searchFilter(query, option)
            : getLabelInternal(option, true, isSelected(option), index)
                .toLowerCase()
                .includes(query.toLowerCase());
        });

  const RootComponent: any = searchable ? Combobox : Listbox;

  const iconNode = icon ? (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground [&_svg]:h-[18px] [&_svg]:w-[18px] [&_svg]:stroke-[2]">
      {icon}
    </span>
  ) : null;

  const controlClassName = classNames(
    "relative flex w-full min-w-0 items-center gap-2 rounded-md border border-border bg-card text-start text-sm font-medium leading-6 shadow-none transition-all duration-150 hover:bg-muted focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/10",
    wrapMultiple ? "min-h-11 py-1.5" : "h-11",
    isFilterControl
      ? "px-3 text-muted-foreground hover:border-primary/30 hover:text-foreground"
      : "px-4 text-foreground",
    controlDir === "rtl" ? "text-right" : "text-left",
    error &&
      "border-destructive focus-within:border-destructive focus-within:ring-destructive/20 focus-visible:border-destructive focus-visible:ring-destructive/20",
    success &&
      "border-success focus-within:border-success focus-visible:border-success",
  );

  const optionsList = filteredOptions.map((option, index) => (
    <Option
      key={getOptionKey(option, index)}
      value={option}
      searchable={searchable}
    >
      {!multiple && isSelected(option) && (
        <span className="absolute inset-y-0 end-0 flex items-center pe-4 text-primary">
          <IconCheck size={18} stroke={2} />
        </span>
      )}
      {getLabelInternal(option as any, true, isSelected(option), index)}
    </Option>
  ));

  const errorMessage = error
    ? typeof error === "string"
      ? error
      : typeof error === "object"
        ? error.message
        : false
    : false;

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.keyCode !== 13 || e.key !== "Enter") {
      if (e.key === "Backspace" && e.currentTarget?.value === "") {
        props.onChange(
          multiple ? (value as Value[]).slice(0, -1) : (undefined as any),
        );
      }

      return;
    }
    if (filteredOptions.length === 0) e.preventDefault();
    onSubmit?.(e.currentTarget.value);
    e.currentTarget.value = "";
    setQuery("");
  };

  return (
    <RootComponent
      ref={ref}
      value={value ? value : multiple ? [] : null}
      multiple={multiple}
      as="div"
      className={classNames(
        size === "large" ? "text-base" : "",
        size !== "large" && "text-sm leading-6",
        !autoWidth && "w-full",
        className,
      )}
      {...props}
      dir={controlDir}
    >
      {({ open }: any) => (
        <>
          {label && (
            <RootComponent.Label as="div">
              <Label label={label} className={className} />
            </RootComponent.Label>
          )}
          <div
            className="relative h-full min-w-0 text-sm leading-6"
            dir={controlDir}
          >
            {searchable ? (
              <div
                className={classNames(controlClassName, !autoWidth && "w-full")}
              >
                {iconPosition === "start" && iconNode}
                {multiple ? (
                  <Combobox.Button
                    className={classNames(
                      "flex w-full min-w-0 flex-1 items-center gap-1.5",
                      wrapMultiple
                        ? "flex-wrap"
                        : "flex-nowrap overflow-hidden",
                    )}
                    as="div"
                  >
                    {maxItemShow != 0 &&
                      (value as any).map((v: any, index: number) => {
                        if (maxItemShow != undefined) {
                          if (index > maxItemShow) {
                            return null;
                          }
                          if (index === maxItemShow) {
                            return (
                              <div
                                className="flex h-7 items-center rounded-md bg-muted px-2 text-xs font-semibold text-muted-foreground"
                                key="last"
                              >
                                ...
                              </div>
                            );
                          }
                        }

                        return (
                          <span
                            className={classNames(
                              "flex h-7 max-w-full overflow-hidden rounded-md bg-muted text-xs font-semibold text-foreground",
                              wrapMultiple && "shrink-0",
                            )}
                            key={getOptionKey(v, index)}
                          >
                            <div className="flex min-w-0 items-center truncate px-2">
                              {getLabelInternal(v, false, isSelected(v), index)}
                            </div>
                            <div
                              className="flex shrink-0 cursor-pointer items-center border-s border-border p-1.5 text-muted-foreground hover:bg-background"
                              onClick={(event) => {
                                event.stopPropagation();
                                const keyFn = getKey as (x: Value) => unknown;
                                const newValue = keyFn
                                  ? (value as Value[]).filter(
                                      (item) => keyFn(item) !== keyFn(v),
                                    )
                                  : (value as Value[]).filter(
                                      (item) => item !== v,
                                    );
                                props.onChange(newValue as any);
                              }}
                            >
                              <IconX size={14} stroke={2} />
                            </div>
                          </span>
                        );
                      })}
                    <div
                      className={classNames(
                        "inline-grid min-w-0 flex-1",
                        wrapMultiple && "min-w-[4rem]",
                        (maxItemShow == 0 || (value as Value[]).length === 0) &&
                          "w-full",
                      )}
                    >
                      <Combobox.Input
                        dir={controlDir}
                        className={classNames(
                          "col-span-1 col-start-1 row-span-1 row-start-1 min-w-0 rounded-none border-none bg-transparent p-0 text-start text-sm font-medium leading-6 text-foreground placeholder:text-start placeholder:text-muted-foreground focus:ring-0",
                          controlDir === "rtl" ? "text-right" : "text-left",
                          !autoWidth && "block w-full",
                          (maxItemShow == 0 ||
                            (value as Value[]).length === 0) &&
                            "w-full",
                          size === "large" ? "text-base" : "",
                        )}
                        displayValue={(option: any) =>
                          !multiple
                            ? getLabelInternal(
                                option,
                                false,
                                isSelected(option),
                                0,
                              )
                            : undefined
                        }
                        placeholder={
                          maxItemShow == 0 || (value as Value[]).length === 0
                            ? placeholder
                            : undefined
                        }
                        size={(value as Value[]).length !== 0 ? 1 : undefined}
                        onChange={(event) => {
                          setQuery(event.target.value);
                          if (onInputChange?.(event.target.value)) {
                            event.target.value = "";
                          }
                        }}
                        onKeyDown={onKeyDown}
                      />
                      {open && (
                        <span
                          className={classNames(
                            "invisible col-span-1 col-start-1 row-span-1 row-start-1 text-start",
                            size === "large" ? "text-base" : "",
                          )}
                        >
                          {query}
                        </span>
                      )}
                    </div>
                    {loading && (
                      <div className="me-12 flex">
                        <LoadingDots />
                      </div>
                    )}
                    {actionLabel && (
                      <span className="shrink-0 text-xs font-bold leading-5 text-muted-foreground">
                        {actionLabel}
                      </span>
                    )}
                    {hideMenu !== true && (
                      <IconChevronDown
                        className="shrink-0 text-muted-foreground"
                        size={18}
                        stroke={2}
                        aria-hidden="true"
                      />
                    )}
                  </Combobox.Button>
                ) : (
                  <Combobox.Button
                    className="flex w-full min-w-0 flex-1 items-center"
                    as="div"
                  >
                    <>
                      <Combobox.Input
                        dir={controlDir}
                        className={classNames(
                          "w-full min-w-0 rounded-none border-none bg-transparent p-0 text-start text-sm font-medium leading-6 text-foreground placeholder:text-muted-foreground focus:ring-0",
                          controlDir === "rtl" ? "text-right" : "text-left",
                          !autoWidth && "block w-full",
                        )}
                        displayValue={(option: any) =>
                          !multiple && option
                            ? getLabelInternal(
                                option,
                                false,
                                isSelected(option),
                                0,
                              )
                            : undefined
                        }
                        onChange={(event) => {
                          setQuery(event.target.value);
                          if (onInputChange?.(event.target.value)) {
                            event.target.value = "";
                          }
                        }}
                        placeholder={placeholder}
                        onKeyDown={onKeyDown}
                      />
                    </>
                    {loading && (
                      <div className="me-12 flex">
                        <LoadingDots />
                      </div>
                    )}
                    {actionLabel && (
                      <span className="shrink-0 text-xs font-bold leading-5 text-muted-foreground">
                        {actionLabel}
                      </span>
                    )}
                    {hideMenu !== true && (
                      <IconChevronDown
                        className="shrink-0 text-muted-foreground"
                        size={18}
                        stroke={2}
                        aria-hidden="true"
                      />
                    )}
                  </Combobox.Button>
                )}
                {iconPosition === "end" && iconNode}
              </div>
            ) : (
              <>
                <Listbox.Button
                  className={classNames(
                    controlClassName,
                    !autoWidth && "w-full",
                  )}
                >
                  {iconPosition === "start" && iconNode}
                  {(Array.isArray(value) &&
                    value.length !== 0 &&
                    maxItemShow !== 0) ||
                  (!Array.isArray(value) &&
                    value !== undefined &&
                    value !== null) ? (
                    multiple ? (
                      <div
                        className={classNames(
                          "flex min-w-0 flex-1 items-center gap-1.5",
                          wrapMultiple ? "flex-wrap" : "overflow-hidden",
                        )}
                      >
                        {(value as any).map((value: any, index: number) => {
                          if (maxItemShow) {
                            if (index > maxItemShow) {
                              return null;
                            }
                            if (index === maxItemShow) {
                              return (
                                <div
                                  className="flex h-7 items-center rounded-md bg-muted px-2 text-xs font-semibold text-muted-foreground"
                                  key="last"
                                >
                                  ...
                                </div>
                              );
                            }
                          }

                          return (
                            <span
                              className={classNames(
                                "flex h-7 min-w-0 max-w-full items-center rounded-md bg-muted px-2 text-xs font-semibold text-foreground",
                                wrapMultiple && "shrink-0",
                              )}
                              key={getOptionKey(value, index)}
                            >
                              <span className="block truncate">
                                {getLabelInternal(
                                  value,
                                  false,
                                  isSelected(value),
                                  index,
                                )}
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="flex min-w-0 flex-1 items-center text-start">
                        <span
                          className={classNames(
                            "block w-full truncate text-start",
                            controlDir === "rtl" ? "text-right" : "text-left",
                          )}
                        >
                          {getLabelInternal(
                            value as any,
                            false,
                            isSelected(value as any),
                            0,
                          )}
                        </span>
                      </span>
                    )
                  ) : (
                    placeholder && (
                      <span className="flex min-w-0 flex-1 items-center text-start text-muted-foreground">
                        <span
                          className={classNames(
                            "block w-full truncate text-start",
                            controlDir === "rtl" ? "text-right" : "text-left",
                          )}
                        >
                          {placeholder}
                        </span>
                      </span>
                    )
                  )}
                  {iconPosition === "end" && iconNode}
                  {actionLabel && (
                    <span className="shrink-0 text-xs font-bold leading-5 text-muted-foreground">
                      {actionLabel}
                    </span>
                  )}
                  {hideMenu !== true && (
                    <IconChevronDown
                      className="shrink-0 text-muted-foreground"
                      size={18}
                      stroke={2}
                      aria-hidden="true"
                    />
                  )}
                </Listbox.Button>
                {loading && <Loading />}
              </>
            )}

            {hideMenu !== true && (
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <RootComponent.Options
                  anchor={optionsAnchor as any}
                  portal
                  dir={portalDir}
                  style={
                    {
                      width: "var(--button-width, var(--input-width))",
                      minWidth: "var(--button-width, var(--input-width))",
                    } as CSSProperties
                  }
                  className="z-popover max-h-72 max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border !border-border bg-card p-1 text-start text-sm leading-6 shadow-medium !outline-none !ring-0 [outline:0] focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0"
                >
                  {children ? children({ options: optionsList }) : optionsList}
                </RootComponent.Options>
              </Transition>
            )}
          </div>

          {errorMessage ? (
            <Text variant="danger" className="mt-2 text-xs">
              {errorMessage}
            </Text>
          ) : success ? (
            <Text variant="success" className="mt-2 text-xs">
              {success}
            </Text>
          ) : null}
        </>
      )}
    </RootComponent>
  );
});

function Option({
  searchable,
  children,
  ...props
}: Parameters<ListBoxType["Option"]>[0] & { searchable?: boolean }) {
  const RootComponent = searchable ? Combobox : Listbox;

  return (
    <RootComponent.Option
      className={({ active, selected }: { [key: string]: any }) =>
        classNames(
          selected
            ? "bg-primary/10 text-primary"
            : active
              ? "bg-muted text-foreground"
              : "text-foreground",
          "relative cursor-pointer select-none rounded-md px-3 py-2.5 text-start text-sm font-medium leading-6",
        )
      }
      {...(props as any)}
    >
      <div className="flex min-w-0 items-center">
        <span className="block w-full truncate">{children as ReactNode}</span>
      </div>
    </RootComponent.Option>
  );
}

interface ComponentSelect {
  <
    Value,
    isMulti extends boolean = false,
    isSearchable extends boolean = false,
  >(
    props: Props<Value, isMulti, isSearchable> & React.RefAttributes<any>,
  ): JSX.Element;
}

export const Select = Object.assign(SelectRoot as ComponentSelect, { Option });
